// SpeakerToggle — a single calm chip that mutes / unmutes the task-
// activation whistle AND the spoken task announcements (course word +
// recited action; see cooking/sounds.ts). It sits next to the
// HandednessToggle in the cooking top bars.
//
// Enabled  → a speaker glyph with sound waves (filled / accent).
// Disabled → the same speaker with a cross through it (outlined / muted).
//
// `aria-pressed` reflects the enabled state; the visible state mirrors the
// active/inactive treatment of the handedness buttons so the two chips read
// as a set.

import { theme } from '../theme';
import { ensureAudioReady } from '../../cooking/sounds';

export interface SpeakerToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

function SpeakerIcon({ enabled }: { enabled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" style={{ display: 'block' }}>
      {/* Speaker body — cone + box. */}
      <path
        d="M4 9v6h4l5 4V5L8 9H4z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.4"
        strokeLinejoin="round"
      />
      {enabled ? (
        // Sound waves.
        <>
          <path
            d="M16 8.5a5 5 0 0 1 0 7"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <path
            d="M18.5 6a8.5 8.5 0 0 1 0 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </>
      ) : (
        // Cross — muted.
        <path
          d="M16 9l5 6M21 9l-5 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export function SpeakerToggle({ enabled, onChange }: SpeakerToggleProps) {
  return (
    <button
      type="button"
      onClick={() => {
        // Unlock audio inside this gesture so the next cue can sound.
        ensureAudioReady();
        onChange(!enabled);
      }}
      aria-pressed={enabled}
      aria-label={enabled ? 'Sound & voice on — mute' : 'Sound & voice off — unmute'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        padding: 0,
        borderRadius: '8px',
        cursor: 'pointer',
        background: enabled ? theme.accent.calm : 'transparent',
        color: enabled ? theme.bg.base : theme.text.muted,
        border: `1px solid ${enabled ? theme.accent.calm : theme.bg.muted}`,
        transition: `background ${theme.motion.duration.fast} ${theme.motion.easing}, color ${theme.motion.duration.fast} ${theme.motion.easing}`,
      }}
    >
      <SpeakerIcon enabled={enabled} />
    </button>
  );
}

export default SpeakerToggle;
